import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { BulkRefreshModal } from '@/components/parts/BulkRefreshModal';
import { NewPartModal } from '@/components/parts/NewPartModal';
import { PartDetailDrawer } from '@/components/parts/PartDetailDrawer';
import { useActiveOrgSlug } from '@/lib/activeOrg';
import { useMe } from '@/lib/auth';
import { cn } from '@/lib/cn';
import {
  type PartCategory,
  type PartListItem,
  type PartsListParams,
  formatMounting,
  usePartCategories,
  usePartDetail,
  usePartsList,
} from '@/lib/parts';
import { type Distributor, useDistributors } from '@/lib/sourcing';
import { supplierLink } from '@/lib/supplierLink';

const PAGE_SIZE = 50;

interface ColumnDef {
  key: string;
  label: string;
  sort?: string;
  align?: 'right';
  render: (p: PartListItem, ctx: { categoryName: (id: number | null) => string }) => React.ReactNode;
}

// Master list — every column Aaron might care about. The visible-set
// (defined below) is what actually renders, persisted to localStorage
// so the user's choice survives reloads.
const ALL_COLUMNS: ColumnDef[] = [
  {
    key: 'part_number',
    label: 'Part #',
    sort: 'part_number',
    render: (p) => <span className="font-mono text-stone-900 dark:text-stone-100">{p.part_number}</span>,
  },
  {
    key: 'category',
    label: 'Category',
    sort: 'category',
    render: (p, { categoryName }) => (
      <span className="text-stone-700 dark:text-stone-300">{categoryName(p.category_id)}</span>
    ),
  },
  {
    key: 'mfg_pn',
    label: 'Mfg PN',
    sort: 'mfg_pn',
    render: (p) => <span className="font-mono">{p.mfg_pn ?? '—'}</span>,
  },
  {
    key: 'manufacturer',
    label: 'Manufacturer',
    sort: 'manufacturer',
    render: (p) => p.manufacturer ?? '—',
  },
  {
    key: 'description',
    label: 'Description',
    sort: 'description',
    render: (p) => (
      <span className="block max-w-xs truncate" title={p.description ?? undefined}>
        {p.description ?? '—'}
      </span>
    ),
  },
  {
    key: 'package',
    label: 'Package',
    sort: 'package',
    render: (p) => p.package ?? '—',
  },
  {
    key: 'mounting',
    label: 'Mounting',
    render: (p) => formatMounting(p.mounting),
  },
  {
    key: 'stock',
    label: 'Stock',
    align: 'right',
    render: (p) => {
      if (!p.inventory) return <span className="text-stone-400 dark:text-stone-500">—</span>;
      const low =
        p.inventory.quantity_threshold > 0 &&
        p.inventory.quantity_on_hand <= p.inventory.quantity_threshold;
      return (
        <span className={cn('font-mono', low && 'text-red-600 font-semibold')}>
          {p.inventory.quantity_on_hand}
          {p.inventory.quantity_is_approximate && '+'}
        </span>
      );
    },
  },
  {
    key: 'lifecycle',
    label: 'Lifecycle',
    sort: 'lifecycle',
    render: (p) => <LifecycleBadge value={p.lifecycle} />,
  },
  {
    key: 'rohs',
    label: 'RoHS',
    align: 'right',
    render: (p) => {
      if (p.rohs_compliant === null) return <span className="text-stone-400 dark:text-stone-500">—</span>;
      return p.rohs_compliant ? '✓' : '✗';
    },
  },
  {
    key: 'kicad',
    label: 'KiCad',
    render: (p) => {
      if (p.kicad_has_3d_model) return <span className="text-green-700">3D</span>;
      if (p.kicad_tracked) return <span className="text-stone-700 dark:text-stone-300">linked</span>;
      return <span className="text-stone-400 dark:text-stone-500">—</span>;
    },
  },
  {
    key: 'updated_at',
    label: 'Updated',
    sort: 'updated_at',
    render: (p) => (
      <span className="text-xs text-stone-500 dark:text-stone-400" title={p.updated_at}>
        {new Date(p.updated_at).toLocaleDateString()}
      </span>
    ),
  },
  // --- Supplier columns ---
  // Show "where the part came from" at a glance. Off by default;
  // toggle via the column picker. The link variant makes the PN
  // clickable when a supplier_url is set.
  {
    key: 'supplier',
    label: 'Supplier',
    render: (p) => p.primary_supplier_name ?? '—',
  },
  {
    key: 'supplier_pn',
    label: 'Distributor PN',
    render: (p) => {
      if (!p.primary_supplier_pn) return '—';
      const href = supplierLink({
        supplier_url: p.primary_supplier_url,
        distributor_slug: p.primary_supplier_slug,
        distributor_pn: p.primary_supplier_pn,
      });
      if (href) {
        return (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="font-mono text-forge-700 dark:text-forge-400 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {p.primary_supplier_pn}
          </a>
        );
      }
      return <span className="font-mono">{p.primary_supplier_pn}</span>;
    },
  },
  {
    key: 'supplier_count',
    label: '# suppliers',
    align: 'right',
    render: (p) => (p.supplier_count ?? 0) || '—',
  },
  // --- Spec columns (off by default, opt-in via the column picker). ---
  // Each renders the value verbatim or "—" when null. Numbers get
  // their unit suffix appended. Off by default to keep the table from
  // exploding into 25+ visible columns; users add what they care about.
  { key: 'alt_mfg_pn', label: 'Alt Mfg PN', render: (p) => <span className="font-mono">{p.alt_mfg_pn ?? '—'}</span> },
  { key: 'alt_manufacturer', label: 'Alt Mfg', render: (p) => p.alt_manufacturer ?? '—' },
  { key: 'subcategory', label: 'Subcategory', render: (p) => p.subcategory ?? '—' },
  { key: 'value', label: 'Value', sort: 'value', render: (p) => p.value ?? '—' },
  { key: 'tolerance', label: 'Tolerance', render: (p) => p.tolerance ?? '—' },
  { key: 'dielectric', label: 'Dielectric / Tempco', render: (p) => p.dielectric ?? '—' },
  { key: 'polarity', label: 'Polarity', render: (p) => p.polarity ?? '—' },
  { key: 'color', label: 'Color', render: (p) => p.color ?? '—' },
  { key: 'voltage_v', label: 'V', align: 'right', render: (p) => p.voltage_v != null ? `${p.voltage_v}V` : '—' },
  { key: 'voltage_v_max', label: 'V max', align: 'right', render: (p) => p.voltage_v_max != null ? `${p.voltage_v_max}V` : '—' },
  { key: 'current_a', label: 'I', align: 'right', render: (p) => p.current_a != null ? `${p.current_a}A` : '—' },
  { key: 'power_w', label: 'P', align: 'right', render: (p) => p.power_w != null ? `${p.power_w}W` : '—' },
  { key: 'vf', label: 'Vf', align: 'right', render: (p) => p.vf != null ? `${p.vf}V` : '—' },
  { key: 'vr', label: 'Vr', align: 'right', render: (p) => p.vr != null ? `${p.vr}V` : '—' },
  { key: 'voltage_clamp_v', label: 'Vc clamp', align: 'right', render: (p) => p.voltage_clamp_v != null ? `${p.voltage_clamp_v}V` : '—' },
  { key: 'frequency_hz', label: 'Frequency', align: 'right', render: (p) => p.frequency_hz != null ? formatHz(p.frequency_hz) : '—' },
  { key: 'wavelength_nm', label: 'Wavelength', align: 'right', render: (p) => p.wavelength_nm != null ? `${p.wavelength_nm}nm` : '—' },
  { key: 'pin_count', label: 'Pins', align: 'right', render: (p) => p.pin_count ?? '—' },
  { key: 'pitch_mm', label: 'Pitch', align: 'right', render: (p) => p.pitch_mm != null ? `${p.pitch_mm}mm` : '—' },
  { key: 'temp_range', label: 'Temp range', render: (p) => formatTempRange(p.temp_min_c, p.temp_max_c) },
  { key: 'output_voltage_v', label: 'Vout', align: 'right', render: (p) => p.output_voltage_v != null ? formatOutputV(p) : '—' },
  { key: 'output_current_a', label: 'Iout', align: 'right', render: (p) => p.output_current_a != null ? `${p.output_current_a}A` : '—' },
  { key: 'regulator_output_type', label: 'Reg type', render: (p) => p.regulator_output_type ?? '—' },
  { key: 'hold_current_a', label: 'Ihold', align: 'right', render: (p) => p.hold_current_a != null ? `${p.hold_current_a}A` : '—' },
  { key: 'trip_current_a', label: 'Itrip', align: 'right', render: (p) => p.trip_current_a != null ? `${p.trip_current_a}A` : '—' },
  // 0014 spec columns.
  { key: 'luminous_intensity_mcd', label: 'Brightness', align: 'right', render: (p) => p.luminous_intensity_mcd != null ? `${p.luminous_intensity_mcd}mcd` : '—' },
  { key: 'load_capacitance_pf', label: 'Cload', align: 'right', render: (p) => p.load_capacitance_pf != null ? `${p.load_capacitance_pf}pF` : '—' },
  { key: 'rds_on_ohms', label: 'RDS(on)', align: 'right', render: (p) => p.rds_on_ohms != null ? `${p.rds_on_ohms}Ω` : '—' },
  { key: 'hfe', label: 'hFE', align: 'right', render: (p) => p.hfe ?? '—' },
];

/** Format a frequency in Hz with the friendliest unit ("12MHz" not
 * "12000000Hz"). Mirrors the backend's display intent for crystals. */
function formatHz(hz: number): string {
  if (hz >= 1e9) return `${(hz / 1e9).toFixed(2).replace(/\.?0+$/, '')}GHz`;
  if (hz >= 1e6) return `${(hz / 1e6).toFixed(2).replace(/\.?0+$/, '')}MHz`;
  if (hz >= 1e3) return `${(hz / 1e3).toFixed(2).replace(/\.?0+$/, '')}kHz`;
  return `${hz}Hz`;
}

function formatTempRange(
  min: number | null | undefined,
  max: number | null | undefined,
): string {
  if (min == null && max == null) return '—';
  return `${min ?? '?'}…${max ?? '?'}°C`;
}

/** Regulator output column shows "3.3V" for fixed parts and the range
 * for adjustable. Compact enough to fit a column even at narrow widths. */
function formatOutputV(p: PartListItem): string {
  const lo = p.output_voltage_v;
  const hi = p.output_voltage_v_max;
  if (lo == null) return '—';
  if (hi == null) return `${lo}V`;
  return `${lo}…${hi}V`;
}

const DEFAULT_VISIBLE: string[] = [
  'part_number',
  'category',
  'mfg_pn',
  'manufacturer',
  'description',
  'package',
  'stock',
  'lifecycle',
];

const COLUMN_PREFS_KEY = 'partforge.parts_columns_v1';

function loadVisibleColumns(): string[] {
  try {
    const raw = localStorage.getItem(COLUMN_PREFS_KEY);
    if (!raw) return DEFAULT_VISIBLE;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_VISIBLE;
    // Filter out any keys that don't exist in ALL_COLUMNS — protects the
    // UI from a stale localStorage entry after we rename or remove a key.
    const known = new Set(ALL_COLUMNS.map((c) => c.key));
    const filtered = parsed.filter((k): k is string => typeof k === 'string' && known.has(k));
    return filtered.length === 0 ? DEFAULT_VISIBLE : filtered;
  } catch {
    return DEFAULT_VISIBLE;
  }
}

function saveVisibleColumns(keys: string[]): void {
  try {
    localStorage.setItem(COLUMN_PREFS_KEY, JSON.stringify(keys));
  } catch {
    /* localStorage unavailable — silently fall back to defaults next load */
  }
}

export function PartsPage() {
  const me = useMe();
  const orgSlug = useActiveOrgSlug(me.data?.organizations);
  const categories = usePartCategories(orgSlug);

  const distributors = useDistributors();

  // URL query params let other pages deep-link into the parts list.
  // Today we honor `?q=` (seed the search box) and `?openId=N` (open
  // that part's drawer immediately). The locations page uses both so a
  // user can click a part inside a location and land in the drawer.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get('q') ?? '';
  const initialOpenId = (() => {
    const raw = searchParams.get('openId');
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  const [searchInput, setSearchInput] = useState(initialQ);
  const [debouncedQ, setDebouncedQ] = useState(initialQ);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState('part_number');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [filterCategoryId, setFilterCategoryId] = useState<number | null>(null);
  const [filterDistributorSlug, setFilterDistributorSlug] = useState<string | null>(
    null,
  );
  const [filterMounting, setFilterMounting] = useState<string | null>(null);
  const [filterLifecycle, setFilterLifecycle] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(initialOpenId);

  // Strip the `openId` param once we've consumed it, so a back-nav
  // followed by manual close-then-reopen doesn't re-trigger the drawer.
  // Leave `q` in the URL so the visible search reflects the URL state.
  useEffect(() => {
    if (initialOpenId !== null) {
      const next = new URLSearchParams(searchParams);
      next.delete('openId');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Multi-row selection — independent of `selectedId` (drawer). Used
  // for bulk actions; clears whenever the rendered list changes
  // shape (filter / search / page) so a "select 50, scroll, refresh,
  // surprise" footgun can't fire.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkRefreshOpen, setBulkRefreshOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    loadVisibleColumns(),
  );

  useEffect(() => {
    saveVisibleColumns(visibleColumns);
  }, [visibleColumns]);

  // 250ms debounce on search input.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(searchInput.trim());
      setPage(0);
    }, 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 0 whenever filters change so the user lands on
  // results, not on an empty offset of a smaller filtered set.
  useEffect(() => {
    setPage(0);
  }, [filterCategoryId, filterDistributorSlug, filterMounting, filterLifecycle]);

  // Drop bulk selection whenever the rendered subset of parts
  // changes — preserving it across filter changes would let the
  // user "select 50 → search → not see them anymore → bulk-action
  // them by mistake".
  useEffect(() => {
    setSelectedIds(new Set());
  }, [
    debouncedQ,
    sort,
    order,
    page,
    filterCategoryId,
    filterDistributorSlug,
    filterMounting,
    filterLifecycle,
  ]);

  const params = useMemo<PartsListParams>(
    () => ({
      q: debouncedQ || undefined,
      category_id: filterCategoryId ?? undefined,
      distributor_slug: filterDistributorSlug ?? undefined,
      mounting: filterMounting ?? undefined,
      lifecycle: filterLifecycle ?? undefined,
      sort,
      order,
      offset: page * PAGE_SIZE,
      limit: PAGE_SIZE,
    }),
    [
      debouncedQ,
      filterCategoryId,
      filterDistributorSlug,
      filterMounting,
      filterLifecycle,
      sort,
      order,
      page,
    ],
  );

  const list = usePartsList(orgSlug, params);
  const detail = usePartDetail(orgSlug, selectedId);

  const total = list.data?.total ?? 0;
  const items = list.data?.items ?? [];
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  const visibleSet = useMemo(() => new Set(visibleColumns), [visibleColumns]);
  const columns = useMemo(
    () => ALL_COLUMNS.filter((c) => visibleSet.has(c.key)),
    [visibleSet],
  );

  const categoryMap = useMemo(() => {
    const m = new Map<number, string>();
    for (const c of categories.data ?? []) m.set(c.id, displayCategoryName(c, categories.data));
    return m;
  }, [categories.data]);

  const onHeaderClick = (sortKey: string | undefined) => {
    if (!sortKey) return;
    if (sort === sortKey) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(sortKey);
      setOrder('asc');
    }
  };

  const toggleRowSelection = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allOnPageSelected =
    items.length > 0 && items.every((p) => selectedIds.has(p.id));
  const someOnPageSelected =
    items.some((p) => selectedIds.has(p.id)) && !allOnPageSelected;
  const togglePageSelection = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        for (const p of items) next.delete(p.id);
      } else {
        for (const p of items) next.add(p.id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const renderCtx = {
    categoryName: (id: number | null) => (id == null ? '—' : categoryMap.get(id) ?? '—'),
  };

  return (
    <div className="space-y-4">
      <div className="flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Parts</h1>
        <div className="flex items-center gap-2">
          <Input
            type="search"
            placeholder="Search description, mfg PN, manufacturer…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-80"
          />
          <ColumnPicker
            visible={visibleColumns}
            onChange={setVisibleColumns}
          />
          <Link
            to="/parts/import"
            className="inline-flex items-center justify-center rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm font-medium text-stone-800 dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-stone-800"
          >
            Import…
          </Link>
          <Button onClick={() => setCreating(true)} disabled={!orgSlug}>
            + New part
          </Button>
        </div>
      </div>

      <FilterBar
        categories={categories.data ?? []}
        categoryName={(id) => categoryMap.get(id) ?? `#${id}`}
        distributors={distributors.data ?? []}
        filterCategoryId={filterCategoryId}
        setFilterCategoryId={setFilterCategoryId}
        filterDistributorSlug={filterDistributorSlug}
        setFilterDistributorSlug={setFilterDistributorSlug}
        filterMounting={filterMounting}
        setFilterMounting={setFilterMounting}
        filterLifecycle={filterLifecycle}
        setFilterLifecycle={setFilterLifecycle}
        resultCount={total}
      />

      {selectedIds.size > 0 && (
        <div className="sticky top-0 z-20 flex flex-wrap items-center gap-3 rounded-md border border-forge-200 dark:border-forge-700 bg-forge-50 dark:bg-forge-900/30 px-3 py-2 text-sm">
          <span className="font-medium text-forge-800 dark:text-forge-300">
            {selectedIds.size} selected
          </span>
          <Button
            variant="secondary"
            onClick={() => setBulkRefreshOpen(true)}
            disabled={!orgSlug}
          >
            Refresh from supplier
          </Button>
          <Button
            variant="ghost"
            onClick={clearSelection}
            className="ml-auto"
          >
            Clear selection
          </Button>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-sm">
        <div className="max-h-[70vh] overflow-y-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 z-10 bg-stone-50 dark:bg-stone-900 text-xs uppercase text-stone-500 dark:text-stone-400 shadow-[inset_0_-1px_0_0] shadow-stone-200">
              <tr>
                <th className="w-10 px-3 py-2">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    ref={(el) => {
                      // `indeterminate` is a runtime-only DOM property —
                      // tri-state visual when some-but-not-all rows on
                      // the page are selected.
                      if (el) el.indeterminate = someOnPageSelected;
                    }}
                    onChange={togglePageSelection}
                    aria-label="Select all on page"
                    className="h-4 w-4 rounded border-stone-300 dark:border-stone-700 text-forge-600 focus:ring-forge-500"
                  />
                </th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => onHeaderClick(col.sort)}
                    className={cn(
                      'px-3 py-2 font-medium tracking-wide',
                      col.align === 'right' && 'text-right',
                      col.sort && 'cursor-pointer select-none hover:text-stone-800 dark:hover:text-stone-100',
                    )}
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.label}
                      {sort === col.sort && (
                        <span aria-hidden>{order === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.isPending && (
                <tr>
                  <td colSpan={columns.length + 1} className="px-3 py-8 text-center text-stone-400 dark:text-stone-500">
                    Loading…
                  </td>
                </tr>
              )}
              {list.isError && (
                <tr>
                  <td colSpan={columns.length + 1} className="px-3 py-8 text-center text-red-600">
                    {(list.error as Error).message}
                  </td>
                </tr>
              )}
              {list.data && items.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="px-3 py-8 text-center text-stone-500 dark:text-stone-400">
                    {debouncedQ ? `No parts match “${debouncedQ}”.` : 'No parts yet.'}
                  </td>
                </tr>
              )}
              {items.map((part) => {
                const checked = selectedIds.has(part.id);
                const isObsolete = part.lifecycle === 'obsolete';
                return (
                  <tr
                    key={part.id}
                    onClick={() => setSelectedId(part.id)}
                    className={cn(
                      'cursor-pointer border-t border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-800',
                      checked && 'bg-forge-50/60 dark:bg-forge-900/15',
                      // EOL parts get a muted treatment so they read as
                      // "still here, but parked" without disappearing
                      // from the list. Aaron's rule: never delete data,
                      // surface state visibly instead.
                      isObsolete && 'opacity-60',
                    )}
                  >
                    <td
                      className="w-10 px-3 py-2 align-top"
                      onClick={(e) => {
                        // Don't open the drawer when clicking the
                        // checkbox cell — only the row body should
                        // route to the drawer.
                        e.stopPropagation();
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleRowSelection(part.id)}
                        aria-label={`Select ${part.part_number}`}
                        className="h-4 w-4 rounded border-stone-300 dark:border-stone-700 text-forge-600 focus:ring-forge-500"
                      />
                    </td>
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          'px-3 py-2 align-top',
                          col.align === 'right' && 'text-right',
                        )}
                      >
                        {col.render(part, renderCtx)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 px-3 py-2 text-sm text-stone-600 dark:text-stone-400">
          <span>
            {total === 0
              ? 'No results'
              : `Showing ${page * PAGE_SIZE + 1}–${Math.min(
                  total,
                  (page + 1) * PAGE_SIZE,
                )} of ${total}`}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Prev
            </Button>
            <span className="text-stone-500 dark:text-stone-400">
              {page + 1} / {lastPage + 1}
            </span>
            <Button
              variant="secondary"
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              disabled={page >= lastPage}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      <PartDetailDrawer
        orgSlug={orgSlug}
        partId={selectedId}
        detail={detail.data}
        loading={detail.isPending && selectedId !== null}
        onClose={() => setSelectedId(null)}
        onDeleted={() => setSelectedId(null)}
      />

      {creating && (
        <NewPartModal
          orgSlug={orgSlug}
          onClose={() => setCreating(false)}
          onCreated={(partId) => {
            setCreating(false);
            setSelectedId(partId);
          }}
        />
      )}

      {bulkRefreshOpen && orgSlug && (
        <BulkRefreshModal
          orgSlug={orgSlug}
          partIds={Array.from(selectedIds)}
          onClose={() => setBulkRefreshOpen(false)}
          openPartId={selectedId}
          onOpenPart={(partId) => {
            // Open the drawer over the bulk modal — don't dismiss the
            // results. Closing the drawer brings the user back to the
            // same results list so they can keep walking through
            // parts that need attention without re-running the bulk
            // refresh.
            setSelectedId(partId);
          }}
        />
      )}
    </div>
  );
}

function ColumnPicker({
  visible,
  onChange,
}: {
  visible: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const visibleSet = new Set(visible);
  const toggle = (key: string) => {
    if (visibleSet.has(key)) {
      // Always keep at least one column visible — an empty table looks broken.
      if (visible.length === 1) return;
      onChange(visible.filter((k) => k !== key));
    } else {
      // Insert in the master-list order so the table doesn't shuffle on
      // every toggle.
      const masterOrder = ALL_COLUMNS.map((c) => c.key);
      const next = [...visible, key].sort(
        (a, b) => masterOrder.indexOf(a) - masterOrder.indexOf(b),
      );
      onChange(next);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center justify-center rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm font-medium text-stone-800 dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-stone-800"
      >
        Columns ({visible.length})
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-56 rounded-md border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-2 shadow-lg">
          <p className="mb-1 px-2 text-xs uppercase tracking-wide text-stone-500 dark:text-stone-400">
            Show columns
          </p>
          {ALL_COLUMNS.map((col) => (
            <label
              key={col.key}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-stone-50 dark:hover:bg-stone-800"
            >
              <input
                type="checkbox"
                checked={visibleSet.has(col.key)}
                onChange={() => toggle(col.key)}
                className="h-4 w-4 rounded border border-stone-300 dark:border-stone-700 text-forge-600 focus:ring-forge-500"
              />
              {col.label}
            </label>
          ))}
          <button
            type="button"
            onClick={() => onChange(DEFAULT_VISIBLE)}
            className="mt-1 w-full rounded px-2 py-1 text-left text-xs text-stone-500 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800"
          >
            Reset to defaults
          </button>
        </div>
      )}
    </div>
  );
}

function displayCategoryName(
  cat: PartCategory,
  all: PartCategory[] | undefined,
): string {
  if (!cat.parent_id || !all) return cat.name;
  const parent = all.find((c) => c.id === cat.parent_id);
  return parent ? `${parent.name} › ${cat.name}` : cat.name;
}

/** Group categories under their parent for use in an `<optgroup>`-style
 * dropdown. Top-level categories (no parent) render as flat options
 * because they typically *are* the leaves a user wants to filter on
 * (e.g. "Connector"). Children render under their parent group. */
function groupCategoriesForFilter(cats: PartCategory[]): {
  topLevel: PartCategory[];
  groups: { parent: PartCategory; children: PartCategory[] }[];
} {
  const topLevel: PartCategory[] = [];
  const childrenOf = new Map<number, PartCategory[]>();
  for (const c of cats) {
    if (c.parent_id == null) {
      topLevel.push(c);
    } else {
      const arr = childrenOf.get(c.parent_id) ?? [];
      arr.push(c);
      childrenOf.set(c.parent_id, arr);
    }
  }
  topLevel.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  const groups: { parent: PartCategory; children: PartCategory[] }[] = [];
  for (const parent of topLevel) {
    const children = (childrenOf.get(parent.id) ?? []).sort(
      (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name),
    );
    if (children.length > 0) groups.push({ parent, children });
  }
  // Top-level entries with no children are listed flat at the top; the
  // ones with children also appear flat (filter "all Capacitors")
  // alongside their child group, so the user can pick either.
  return { topLevel, groups };
}

// Same lists used in EditPartModal / NewPartModal so the filter dropdowns
// surface every value the schema accepts. Mirrors the backend's `Mounting`
// and `Lifecycle` enum members from `app/models/parts.py`.
const MOUNTING_OPTIONS = [
  { value: 'smd', label: 'SMD' },
  { value: 'tht', label: 'Through-hole' },
  { value: 'chassis', label: 'Chassis' },
  { value: 'other', label: 'Other' },
];
const LIFECYCLE_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'nrnd', label: 'NRND' },
  { value: 'obsolete', label: 'Obsolete (EOL)' },
  { value: 'preview', label: 'Preview' },
  { value: 'unknown', label: 'Unknown' },
];

function FilterBar({
  categories,
  categoryName,
  distributors,
  filterCategoryId,
  setFilterCategoryId,
  filterDistributorSlug,
  setFilterDistributorSlug,
  filterMounting,
  setFilterMounting,
  filterLifecycle,
  setFilterLifecycle,
  resultCount,
}: {
  categories: PartCategory[];
  categoryName: (id: number) => string;
  distributors: Distributor[];
  filterCategoryId: number | null;
  setFilterCategoryId: (id: number | null) => void;
  filterDistributorSlug: string | null;
  setFilterDistributorSlug: (slug: string | null) => void;
  filterMounting: string | null;
  setFilterMounting: (value: string | null) => void;
  filterLifecycle: string | null;
  setFilterLifecycle: (value: string | null) => void;
  resultCount: number;
}) {
  const { topLevel, groups } = useMemo(
    () => groupCategoriesForFilter(categories),
    [categories],
  );
  const hasActive =
    filterCategoryId !== null ||
    filterDistributorSlug !== null ||
    filterMounting !== null ||
    filterLifecycle !== null;
  void categoryName; // exposed for future "active filter chip" rendering

  const onCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    setFilterCategoryId(v === '' ? null : Number(v));
  };

  const onDistributorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    setFilterDistributorSlug(v === '' ? null : v);
  };

  const onMountingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterMounting(e.target.value === '' ? null : e.target.value);
  };

  const onLifecycleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterLifecycle(e.target.value === '' ? null : e.target.value);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-stone-500 dark:text-stone-400">Filter:</span>
      <select
        value={filterCategoryId ?? ''}
        onChange={onCategoryChange}
        className="rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1.5 text-sm"
        aria-label="Filter by category"
      >
        <option value="">All categories</option>
        {topLevel
          .filter((p) => !groups.some((g) => g.parent.id === p.id))
          .map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        {groups.map(({ parent, children }) => (
          <optgroup key={parent.id} label={parent.name}>
            <option value={parent.id}>All {parent.name}</option>
            {children.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      <select
        value={filterDistributorSlug ?? ''}
        onChange={onDistributorChange}
        className="rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1.5 text-sm"
        aria-label="Filter by supplier"
      >
        <option value="">All suppliers</option>
        {distributors.map((d) => (
          <option key={d.id} value={d.slug}>
            {d.name}
          </option>
        ))}
      </select>

      <select
        value={filterMounting ?? ''}
        onChange={onMountingChange}
        className="rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1.5 text-sm"
        aria-label="Filter by mounting"
      >
        <option value="">All mounting</option>
        {MOUNTING_OPTIONS.map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </select>

      <select
        value={filterLifecycle ?? ''}
        onChange={onLifecycleChange}
        className="rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1.5 text-sm"
        aria-label="Filter by lifecycle"
      >
        <option value="">All lifecycle</option>
        {LIFECYCLE_OPTIONS.map((l) => (
          <option key={l.value} value={l.value}>
            {l.label}
          </option>
        ))}
      </select>

      {hasActive && (
        <>
          <button
            type="button"
            onClick={() => {
              setFilterCategoryId(null);
              setFilterDistributorSlug(null);
              setFilterMounting(null);
              setFilterLifecycle(null);
            }}
            className="text-xs text-forge-700 dark:text-forge-400 hover:underline"
          >
            Clear filters
          </button>
          <span className="text-xs text-stone-500 dark:text-stone-400">
            ({resultCount} {resultCount === 1 ? 'match' : 'matches'})
          </span>
        </>
      )}
    </div>
  );
}

function LifecycleBadge({ value }: { value: string }) {
  const styles: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    nrnd: 'bg-amber-100 text-amber-800',
    obsolete: 'bg-red-100 text-red-800',
    preview: 'bg-blue-100 text-blue-800',
    unknown: 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400',
  };
  return (
    <span
      className={cn(
        'inline-flex rounded px-2 py-0.5 text-xs font-medium',
        styles[value] ?? styles.unknown,
      )}
    >
      {value}
    </span>
  );
}
